import { useState, useEffect } from 'react';
import { auth } from '@/lib/firebase';

interface ImpersonationData {
  superAdminUid: string;
  superAdminEmail: string;
  targetUid: string;
  targetEmail: string;
  targetCompanyId: string;
  targetCompanyName: string;
  startedAt: { seconds: number; nanoseconds: number };
  expiresAt: { seconds: number; nanoseconds: number };
}

interface TargetUser {
  uid: string;
  email: string;
  displayName: string;
  role: string;
  companyId: string;
  companyName: string;
}

const IMPERSONATION_KEY = 'impersonation_data';
const TARGET_USER_KEY = 'impersonation_target_user';

export function useImpersonation() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState<ImpersonationData | null>(null);
  const [targetUser, setTargetUser] = useState<TargetUser | null>(null);

  // Check impersonation status on mount and when auth state changes
  useEffect(() => {
    const checkImpersonation = () => {
      const storedData = localStorage.getItem(IMPERSONATION_KEY);
      const storedTargetUser = localStorage.getItem(TARGET_USER_KEY);

      if (storedData && storedTargetUser) {
        try {
          const data: ImpersonationData = JSON.parse(storedData);
          const target: TargetUser = JSON.parse(storedTargetUser);

          // Check if session has expired
          const now = Date.now();
          const expiresAt = data.expiresAt.seconds * 1000;

          if (now >= expiresAt) {
            // Session expired, clear it
            endImpersonation();
            return;
          }

          setIsImpersonating(true);
          setImpersonationData(data);
          setTargetUser(target);
        } catch (error) {
          console.error('[useImpersonation] Error parsing impersonation data:', error);
          // Clear invalid data
          localStorage.removeItem(IMPERSONATION_KEY);
          localStorage.removeItem(TARGET_USER_KEY);
        }
      } else {
        setIsImpersonating(false);
        setImpersonationData(null);
        setTargetUser(null);
      }
    };

    checkImpersonation();

    // Re-check when storage changes (in case of multi-tab scenarios)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === IMPERSONATION_KEY || e.key === TARGET_USER_KEY) {
        checkImpersonation();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const startImpersonation = async (companyId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { success: false, error: 'Not authenticated' };
      }

      const token = await user.getIdToken();

      const response = await fetch('/api/admin/impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ companyId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to start impersonation' };
      }

      // Store impersonation data
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(data.impersonation));
      localStorage.setItem(TARGET_USER_KEY, JSON.stringify(data.targetUser));

      setIsImpersonating(true);
      setImpersonationData(data.impersonation);
      setTargetUser(data.targetUser);

      // Reload the page to apply new context
      window.location.href = '/app';

      return { success: true };
    } catch (error) {
      console.error('[useImpersonation] Error starting impersonation:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const endImpersonation = async (): Promise<void> => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();

        // Call API to log the end of impersonation
        await fetch('/api/admin/impersonate', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('[useImpersonation] Error ending impersonation:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem(TARGET_USER_KEY);

      setIsImpersonating(false);
      setImpersonationData(null);
      setTargetUser(null);

      // Reload to return to super_admin context
      window.location.href = '/app/tenants/customers';
    }
  };

  return {
    isImpersonating,
    impersonationData,
    targetUser,
    startImpersonation,
    endImpersonation,
  };
}
